
import React from "react";
import { Button } from "@/components/ui/button";
import { TableBody, TableCell, TableRow } from "@/components/ui/table";
import { Copy } from "lucide-react";
import { InventoryItem, getMagazijnLabel } from "@/types/inventory";
import { copyToClipboard } from "@/utils/clipboard";
import { ActionButtons } from "./ActionButtons";
import { generateInventoryItemKey } from "@/utils/inventory/sortUtils";
import { MOQDisplay } from "../MOQDisplay";

interface InventoryTableBodyProps {
  results: InventoryItem[];
}

export const InventoryTableBody: React.FC<InventoryTableBodyProps> = ({ results }) => {
  if (results.length === 0) {
    return (
      <TableBody>
        <TableRow>
          <TableCell colSpan={9} className="text-center text-muted-foreground">
            Geen producten gevonden
          </TableCell>
        </TableRow>
      </TableBody>
    );
  }

  return (
    <TableBody>
      {results.map((result, index) => {
        const uniqueKey = generateInventoryItemKey(result, index);
        
        return (
          <TableRow key={uniqueKey}>
            <TableCell className="font-medium">
              <div className="flex items-center gap-1">
                {result.product_naam}
                <Button 
                  variant="ghost" 
                  size="icon" 
                  className="h-5 w-5 ml-1" 
                  onClick={() => copyToClipboard(result.product_naam, "Productnaam")}
                >
                  <Copy className="h-3 w-3" />
                  <span className="sr-only">Kopieer productnaam</span>
                </Button>
              </div>
            </TableCell>
            <TableCell>
              <div className="flex items-center gap-1">
                {result.product_id}
                <Button 
                  variant="ghost" 
                  size="icon" 
                  className="h-5 w-5" 
                  onClick={() => copyToClipboard(result.product_id, "Artikelnummer")}
                >
                  <Copy className="h-3 w-3" />
                  <span className="sr-only">Kopieer artikelnummer</span>
                </Button>
              </div>
            </TableCell>
            <TableCell>
              {result.bc && (
                <div className="flex items-center gap-1">
                  {result.bc}
                  <Button 
                    variant="ghost" 
                    size="icon" 
                    className="h-5 w-5" 
                    onClick={() => copyToClipboard(result.bc || "", "Barcode")}
                  >
                    <Copy className="h-3 w-3" />
                    <span className="sr-only">Kopieer barcode</span>
                  </Button>
                </div>
              )}
            </TableCell>
            <TableCell>{result.eenheid}</TableCell>
            <TableCell className={result.magazijn === 2 ? "text-blue-500 font-medium" : ""}>
              {getMagazijnLabel(result.magazijn)}
            </TableCell>
            <TableCell>
              {result.locatie && (
                <div className="flex items-center gap-1">
                  {result.locatie}
                  <Button 
                    variant="ghost" 
                    size="icon" 
                    className="h-5 w-5" 
                    onClick={() => copyToClipboard(result.locatie || "", "Locatie")}
                  >
                    <Copy className="h-3 w-3" />
                    <span className="sr-only">Kopieer locatie</span>
                  </Button>
                </div>
              )}
            </TableCell>
            <TableCell 
              className={`font-medium ${result.voorraad > 0 ? 'text-green-500' : 'text-red-500'}`}
            >
              {result.voorraad}
            </TableCell>
            <TableCell>
              <MOQDisplay moq={result.moq} />
            </TableCell>
            <TableCell>
              <ActionButtons item={result} />
            </TableCell>
          </TableRow>
        );
      })}
    </TableBody>
  );
};
